import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FolderOpen, X } from "lucide-react";
import { useAuthContext } from "../hooks/useAuthContext";
import LibraryHero from "../components/library/LibraryHero";
import LibraryRecommendations from "../components/library/LibraryRecommendations";
import LibraryFeed from "../components/library/LibraryFeed";
import SubjectBrowser from "../components/library/SubjectBrowser";
import CreateQuizModal from "../components/library/CreateQuizModal";
import LibraryRightSidebar from "../components/library/LibraryRightSidebar";
import BrowseAllView from "../components/library/BrowseAllView";
import FolderDiscoverView from "../components/library/FolderDiscoverView";
import EditMaterialModal from "../components/library/EditMaterialModal";
import AIResultModal from "../components/pdf/AIResultModal";

// Shapes a raw Pdf row from the API into what the card components expect.
const mapMaterial = (r) => {
  const type = (r.category || "Note").toLowerCase();
  const rawRating = r.rating || 0;
  // Ratings are stored 0-10; the cards expect a 0-5 star scale.
  const normalizedRating = rawRating > 5 ? (rawRating / 2).toFixed(1) : rawRating.toFixed(1);

  return {
    ...r,
    type,
    excerpt: r.description || "No description available.",
    uploader: r.author || "Anonymous",
    uploaderInitials: (r.author || "A").split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase(),
    university: r.university || "Academic Institution",
    rating: normalizedRating,
    ratingCount: r.reviewsCount || 0,
    pages: r.pages || 0,
    readTime: r.readTime ? `${r.readTime} min` : "N/A",
  };
};

const Library = ({ scrollContainerRef, setShowSearchNavbar }) => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuthContext();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(""); // Local state
  const [activeFilters, setActiveFilters] = useState({
    types: ["all"],
    sources: ["all"],
    years: ["All"],
    levels: ["All"],
    minRating: 0,
  });
  const [sortBy, setSortBy] = useState("newest");
  const [saved, setSaved] = useState([]);
  const [quizResourceId, setQuizResourceId] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [contentVisible, setContentVisible] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(false); // Local state
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [browseAllOpen, setBrowseAllOpen] = useState(false);
  const [browseUniversity, setBrowseUniversity] = useState("All");
  const [universityOptions, setUniversityOptions] = useState([]);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [trending, setTrending] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const contentRef = useRef(null);

  const buildQueryParams = useCallback((overrides = {}) => {
    const type = activeFilters.types.includes("all") ? "All" : activeFilters.types[0];
    const categoryMap = { 'lectures': 'Note', 'exercises': 'Exercise', 'courses': 'Book', 'quizzes': 'Quiz' };
    const backendCategory = categoryMap[type] || 'All';

    const source = activeFilters.sources.includes("all") ? "All" : activeFilters.sources[0];
    const year = activeFilters.years.includes("All") ? "All" : activeFilters.years[0];
    const level = activeFilters.levels.includes("All") ? "All" : activeFilters.levels[0];

    const ownerOnly = activeFilters.sources.includes("my-uploads");
    const savedOnly = activeFilters.sources.includes("my-saved");

    const params = new URLSearchParams({
      search: searchQuery,
      category: backendCategory,
      year,
      level,
      topic: selectedTopic || 'All',
      minRating: activeFilters.minRating,
      sortBy: sortBy === 'highest-rated' ? 'Highest Rated' : (sortBy === 'popular' ? 'Popularity' : 'Newest'),
      limit: 10,
      page: 1,
      ownerOnly,
      savedOnly,
      ...overrides,
    });

    if (source === 'universities') params.append('university', 'Verified');
    if (browseUniversity !== 'All') params.set('university', browseUniversity);

    return params;
  }, [activeFilters, searchQuery, sortBy, selectedTopic, browseUniversity]);

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams({ limit: browseAllOpen ? 50 : 10 });
      const response = await fetch(`/api/materials/all?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMaterials((data.materials || []).map(mapMaterial));
        setTotalResults(data.total || 0);
        setSaved(data.savedIds || []);
      }
    } catch (error) {
      console.error("Error fetching materials:", error);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, browseAllOpen, token]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // "Recommended for You" / "Trending This Week" are their own small,
  // independently-sorted requests (highest rated / most viewed) — not
  // slices of whatever the main filtered feed happens to contain, which
  // used to make every section show the same handful of items.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchHighlights = async () => {
      try {
        const [recRes, trendRes] = await Promise.all([
          fetch(`/api/materials/all?${new URLSearchParams({ sortBy: 'Highest Rated', limit: 4, page: 1 })}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`/api/materials/all?${new URLSearchParams({ sortBy: 'Popularity', limit: 4, page: 1 })}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const [recData, trendData] = await Promise.all([recRes.json(), trendRes.json()]);
        if (cancelled) return;
        setRecommended((recData.materials || []).map(mapMaterial));
        setTrending((trendData.materials || []).map(mapMaterial));
      } catch (error) {
        console.error("Error fetching recommendations:", error);
      }
    };
    fetchHighlights();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch('/api/materials/universities', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : { universities: [] }))
      .then((data) => setUniversityOptions(data.universities || []))
      .catch(() => setUniversityOptions([]));
  }, [token]);

  const quizResource = materials.find(r => r.id === quizResourceId) || null;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setContentVisible(true);
          if (setShowSearchNavbar) setShowSearchNavbar(true);
        } else {
          // If we scroll back up and the archives are out of view (below viewport)
          if (entry.boundingClientRect.top > 0) {
            if (setShowSearchNavbar) setShowSearchNavbar(false);
          }
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -50px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [setShowSearchNavbar]);

  const handleOpenReader = (id) => {
    navigate(`/${lang}/material/${id}`);
  };

  const handleSave = async (id) => {
    try {
      const response = await fetch('/api/materials/toggle-save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pdfId: id })
      });
      if (response.ok) {
        const data = await response.json();
        setSaved(data.savedIds);
      }
    } catch (error) {
      console.error("Error saving material:", error);
    }
  };

  const handleDismiss = (id) => {
    setDismissed(prev => [...prev, id]);
  };

  const [quizResult, setQuizResult] = useState(null);
  const handleQuizGenerated = (data) => {
    setQuizResourceId(null);
    setQuizResult(data);
  };

  const handleSelectSubject = (topic) => {
    setSelectedTopic(topic);
    setTimeout(() => {
      if (scrollContainerRef && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: window.innerHeight - 100,
          behavior: "smooth"
        });
      }
    }, 50);
  };

  const visibleResources = materials.filter(r => !dismissed.includes(r.id));

  return (
    <div className="library-page relative">
      {/* Premium Background Elements (Faded for light theme) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[20%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/[0.03] blur-[120px]" />
        <div className="absolute bottom-[20%] right-[-5%] w-[30%] h-[30%] rounded-full bg-secondary/[0.03] blur-[100px]" />
      </div>

      <AnimatePresence mode="wait">
        {discoverOpen ? (
          <FolderDiscoverView
            key="folder-discover"
            token={token}
            onBack={() => setDiscoverOpen(false)}
          />
        ) : browseAllOpen ? (
          <BrowseAllView
            key="browse-all"
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onBack={() => setBrowseAllOpen(false)}
            sortBy={sortBy}
            onSortChange={setSortBy}
            university={browseUniversity}
            onUniversityChange={setBrowseUniversity}
            universityOptions={universityOptions}
            resources={visibleResources}
            saved={saved}
            onSave={handleSave}
            onOpen={handleOpenReader}
            onCreateQuiz={setQuizResourceId}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            loading={loading}
            totalResults={totalResults}
            currentUserId={user?.id}
            onEdit={setEditingMaterial}
          />
        ) : (
          <motion.div key="browse-normal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <LibraryHero
              query={searchQuery}
              onQueryChange={setSearchQuery}
              filters={activeFilters}
              onFiltersChange={setActiveFilters}
              sortBy={sortBy}
              onSortChange={setSortBy}
              totalResults={totalResults}
              scrollContainerRef={scrollContainerRef}
              showFilters={filtersOpen}
              setShowFilters={setFiltersOpen}
            />

            <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
              <div
                ref={contentRef}
                className={`transition-all duration-1000 ease-out py-8 ${
                  contentVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-12"
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
                  </div>
                ) : (
                  <>
                    <LibraryRecommendations
                      recommended={recommended}
                      trending={trending}
                      onOpen={handleOpenReader}
                      saved={saved}
                      onSave={handleSave}
                      onDismiss={handleDismiss}
                      viewMode={viewMode}
                    />
                    <SubjectBrowser
                      token={token}
                      selectedTopic={selectedTopic}
                      onSelectSubject={handleSelectSubject}
                    />
                    <LibraryFeed
                      resources={visibleResources}
                      saved={saved}
                      onSave={handleSave}
                      onOpen={handleOpenReader}
                      onCreateQuiz={setQuizResourceId}
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      onViewAll={() => setBrowseAllOpen(true)}
                      currentUserId={user?.id}
                      onEdit={setEditingMaterial}
                    />
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Folders lives behind an icon now instead of a permanent sidebar,
          so the resource grid gets the full width. */}
      {!discoverOpen && (
        <button
          onClick={() => setFoldersOpen(true)}
          className="fixed bottom-8 right-8 z-40 w-14 h-14 rounded-2xl bg-primary text-primary-foreground shadow-2xl shadow-primary/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          title="Folders"
        >
          <FolderOpen className="w-6 h-6" />
        </button>
      )}

      <AnimatePresence>
        {foldersOpen && (
          <React.Fragment key="folders-drawer">
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setFoldersOpen(false)}
            />
            <motion.div
              className="fixed top-0 right-0 h-full w-full max-w-[420px] bg-background z-50 shadow-2xl overflow-y-auto p-6"
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground">Your Folders</h2>
                <button
                  onClick={() => setFoldersOpen(false)}
                  className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <LibraryRightSidebar
                onOpenDiscover={() => { setFoldersOpen(false); setDiscoverOpen(true); }}
              />
            </motion.div>
          </React.Fragment>
        )}
      </AnimatePresence>

      {editingMaterial && (
        <EditMaterialModal
          material={editingMaterial}
          token={token}
          onClose={() => setEditingMaterial(null)}
          onSaved={() => fetchMaterials()}
        />
      )}

      {quizResource && (
        <CreateQuizModal
          resourceId={quizResource.id}
          resourceTitle={quizResource.title}
          resourcePages={quizResource.pages}
          token={token}
          onClose={() => setQuizResourceId(null)}
          onGenerated={handleQuizGenerated}
        />
      )}

      <AIResultModal
        isOpen={!!quizResult}
        onClose={() => setQuizResult(null)}
        loading={false}
        error={null}
        title="Quiz"
        textContent=""
        questions={quizResult?.questions}
        materialTitle={quizResult?.materialTitle}
      />

      {/* Footer spacing */}
      <div className="h-32" />
    </div>
  );
};

export default Library;
